import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProducts } from '../hooks/useProducts';
import { useCart } from '../hooks/useCart';
import ProductImageGallery from '../components/product/ProductImageGallery';
import ProductInfo from '../components/product/ProductInfo';
import ProductActions from '../components/product/ProductActions';
import RelatedProducts from '../components/product/RelatedProducts';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';
import Toast from '../components/common/Toast';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getProductById } = useProducts();
  const { addToCart } = useCart();

  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [loadingProduct, setLoadingProduct] = useState(true);
  const [productError, setProductError] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  useEffect(() => {
    const fetchProduct = async () => {
      setLoadingProduct(true);
      try {
        const response = await getProductById(id);
        if (response.success) {
          setProduct(response.data.product);
          setRelatedProducts(response.data.relatedProducts || []);
          setProductError(null);
        } else {
          setProductError(response.message || 'Product not found');
        }
      } catch (err) {
        console.error('Error fetching product:', err);
        setProductError(err.message || 'Error loading product');
      } finally {
        setLoadingProduct(false);
      }
    };

    if (id) {
      fetchProduct();
    }
  }, [id, getProductById]);

  const handleAddToCart = () => {
    if (product) {
      addToCart({
        id: product._id,
        name: product.name,
        price: product.price,
        image: product.images[0]?.url || '/placeholder-product.jpg',
        category: product.category,
        quantity: quantity
      });
      setToastMessage(`${product.name} added to cart`);
      setShowToast(true);
    }
  };

  const handleQuantityChange = (newQuantity) => {
    if (newQuantity >= 1 && newQuantity <= (product?.stock || 10)) {
      setQuantity(newQuantity);
    }
  };

  const handleImageSelect = (index) => {
    setSelectedImage(index);
  };

  if (loadingProduct) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (productError) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-16 text-center">
        <ErrorMessage message={productError} />
        <button
          onClick={() => navigate('/')}
          className="mt-6 px-5 py-2 bg-pinkAccent text-white rounded-lg hover:bg-pink-700 transition-colors"
        >
          Back to Home
        </button>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-16 text-center">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Product not found</h2>
        <button
          onClick={() => navigate('/')}
          className="px-5 py-2 bg-pinkAccent text-white rounded-lg hover:bg-pink-700 transition-colors"
        >
          Back to Home
        </button>
      </div>
    );
  }

  return (
    <div className="bg-softRose min-h-screen">
      {/* Toast Notification */}
      {showToast && (
        <Toast 
          message={toastMessage}
          onClose={() => setShowToast(false)}
          duration={3000}
        />
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb Navigation */}
        <nav className="flex text-sm text-slateText mb-6">
          <button 
            onClick={() => navigate('/')}
            className="hover:underline hover:text-pinkAccent transition-colors"
          >
            Home
          </button>
          <span className="mx-2">/</span>
          <button
            onClick={() => navigate(`/category/${product.category}`)}
            className="hover:underline hover:text-pinkAccent transition-colors capitalize"
          >
            {product.category}
          </button>
          <span className="mx-2">/</span>
          <span className="font-medium text-gray-900">{product.name}</span>
        </nav>

        {/* Main Product Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12 bg-white p-6 rounded-lg shadow-sm">
          {/* Image Gallery */}
          <div className="w-full">
            <ProductImageGallery
              images={product.images}
              selectedImage={selectedImage}
              onImageSelect={handleImageSelect}
            />
          </div>

          {/* Product Info */}
          <div className="w-full">
            <ProductInfo 
              product={product} 
              className="mb-6"
            />
            
            <div className="border-t border-gray-200 pt-6">
              <ProductActions
                stock={product.stock}
                price={product.price}
                originalPrice={product.originalPrice}
                quantity={quantity}
                onQuantityChange={handleQuantityChange}
                onAddToCart={handleAddToCart}
                isAvailable={product.isAvailable}
              />
            </div>
          </div>
        </div>

        {/* Product Description */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-12">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">Product Details</h3>
          <div className="prose max-w-none text-gray-700">
            {product.description}
          </div>
          
          {product.features?.length > 0 && (
            <div className="mt-6">
              <h4 className="text-lg font-medium text-gray-900 mb-2">Features</h4>
              <ul className="list-disc pl-5 space-y-1">
                {product.features.map((feature, index) => (
                  <li key={index}>{feature}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">You May Also Like</h3>
            <RelatedProducts 
              products={relatedProducts} 
              onAddToCart={(product) => {
                addToCart({
                  id: product._id,
                  name: product.name,
                  price: product.price,
                  image: product.images[0]?.url || '/placeholder-product.jpg',
                  category: product.category,
                  quantity: 1
                });
                setToastMessage(`${product.name} added to cart`);
                setShowToast(true);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;